import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import DownloadIcon from "@mui/icons-material/Download";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import BusquedaPorIdentificador from "./BusquedaPorIdentificador";
import {
  cambiarEstadoEquipoCentro,
  exportarListadoCentroEquiposExcel,
  listarCentroEquipos,
} from "../services/centroEquiposService";

const descargarBlob = (data, filename) => {
  const url = URL.createObjectURL(new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const TIPOS = [
  { value: "equipo_modem", label: "Modems" },
  { value: "equipo_chip", label: "Chips" },
  { value: "equipo_bateria", label: "Baterias" },
  { value: "equipo_radio", label: "Radios" },
  { value: "equipo_varios", label: "Varios" },
  { value: "todos", label: "Todos" },
];

const ESTADOS = [
  "Activos",
  "Vendido Activo",
  "Vendido",
  "Inactivo",
  "Almacen",
  "Asignados",
  "Traspaso",
  "Transicion",
  "Baja",
  "Mantenimiento",
  "Traspaso Mantenimiento",
  "Robado",
  "Vendido con Servicio",
  "Programado",
];

const SUCURSALES = ["Todos", "La Paz", "La Paz(El Alto)", "Santa Cruz", "Cochabamba", "Beni", "Pando", "Chuquisaca", "Potosi", "Tarija", "Oruro"];

const normalizeSucursal = (value) => {
  const found = SUCURSALES.find((item) => item.toLowerCase() === String(value || "").trim().toLowerCase());
  return found || "Todos";
};

const formatDateTime = (value) => {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}[T\s]/.test(value)) {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/La_Paz",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(date);

  const get = (type) => parts.find((part) => part.type === type)?.value || "00";
  return `${get("year")}-${get("month")}-${get("day")} ${get("hour")}:${get("minute")}:${get("second")}`;
};

const fmt = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const formattedDate = formatDateTime(value);
  if (formattedDate) return formattedDate;
  return String(value);
};

const columnsByType = {
  equipo_modem: [
    ["identificador", "Num sim"],
    ["modelo", "Modelo"],
    ["estado", "Estado"],
    ["sucursal", "Sucursal"],
    ["placa", "Placa"],
    ["chips_detalle", "Chips"],
    ["id_ref", "Alias"],
    ["grupo", "Grupo"],
    ["clientes", "Cliente / Servicio"],
  ],
  equipo_chip: [
    ["identificador", "Numero"],
    ["num", "Num"],
    ["emai", "Emei"],
    ["modelo", "Modelo"],
    ["estado", "Estado"],
    ["modem", "Modem"],
    ["modem_modelo", "Modelo Modem"],
    ["placa", "Placa"],
    ["sucursal", "Sucursal"],
  ],
  equipo_bateria: [
    ["identificador", "Serie"],
    ["modelo", "Modelo"],
    ["marca", "Marca"],
    ["estado", "Estado"],
    ["sucursal", "Sucursal"],
    ["propiedad", "Propiedad"],
    ["observacion", "Observacion"],
  ],
  equipo_radio: [
    ["identificador", "Serie"],
    ["modelo", "Modelo"],
    ["marca", "Marca"],
    ["estado", "Estado"],
    ["sucursal", "Sucursal"],
    ["placa", "Placa"],
    ["grupo", "Grupo"],
    ["id_ref", "Alias"],
  ],
  equipo_varios: [
    ["identificador", "Serie"],
    ["modelo", "Modelo"],
    ["emai", "Emei"],
    ["categoria", "Categoria"],
    ["estado", "Estado"],
    ["cod_servicio", "Cod servicio"],
    ["fecha_registro", "Fecha registro"],
  ],
};

const defaultColumns = [
  ["tipo_equipo", "Tipo"],
  ["identificador", "Identificador"],
  ["modelo", "Modelo"],
  ["estado", "Estado"],
  ["sucursal", "Sucursal"],
  ["placa", "Placa"],
  ["clientes", "Cliente / Servicio"],
];

const controlSx = {
  "& .MuiInputLabel-root": { fontSize: 11 },
  "& .MuiOutlinedInput-root": { height: 30, fontSize: 11.5 },
  "& .MuiSelect-select": { py: 0.35, px: 1 },
};

const cellMaxWidth = (key) => {
  if (key === "clientes") return 280;
  if (key === "chips_detalle") return 240;
  if (key === "modelo") return 220;
  if (key === "identificador" || key === "modem") return 150;
  if (key === "emai") return 190;
  return 125;
};

// El "emai" (IMEI) no debe truncarse: es un dato exacto que el usuario necesita leer
// completo (copiarlo, compararlo), a diferencia de campos largos como "clientes".
const NO_TRUNCATE_COLUMNS = new Set(["emai"]);

const toDetalleTipo = (tipoEquipo) => {
  const value = String(tipoEquipo || "").toLowerCase();
  if (value === "equipo_modem") return "modem";
  if (value === "equipo_chip") return "chip";
  if (value === "equipo_bateria") return "bateria";
  if (value === "equipo_radio") return "radio";
  if (value === "equipo_varios") return "varios";
  return "";
};

const getDetalleId = (row) => row?.identificador || row?.num_sim || row?.numero || row?.serie || row?.id;

const sameText = (a, b) => String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();

const isActionType = (tipo) => ["equipo_modem", "equipo_chip", "equipo_bateria", "equipo_radio", "equipo_varios"].includes(tipo);

const normalizeAccess = (value) =>
  String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLowerCase();

const esEstadoTransicion = (value) => ["transicion", "transsicion", "trancicion"].includes(normalizeAccess(value));

const hasClienteRelacionado = (row) => {
  if (Number(row?.tiene_cliente_relacionado || 0) > 0 || row?.tiene_cliente_relacionado === true) return true;
  const clientes = row?.clientes;
  if (Array.isArray(clientes)) return clientes.length > 0;
  return Boolean(String(clientes || "").trim() && String(clientes || "").trim() !== "-");
};

export default function ListadoEquipos() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  // Deep link desde otra pantalla (ej. el widget "Equipos por recibir" del dashboard,
  // que linkea acá con ?tipo=todos&estado=Transicion&sucursal=...) — si vienen, se usan
  // como filtros iniciales en vez de los de siempre, y se dispara la búsqueda sola.
  const paramTipo = searchParams.get("tipo");
  const paramEstado = searchParams.get("estado");
  const paramSucursal = searchParams.get("sucursal");
  const tieneDeepLinkRef = useRef(Boolean(paramTipo || paramEstado || paramSucursal));
  const initialSucursal = useMemo(() => normalizeSucursal(paramSucursal || user?.sucursal), [paramSucursal, user?.sucursal]);
  const canManageInventory = useMemo(() => {
    const role = normalizeAccess(user?.role);
    const subRol = normalizeAccess(user?.sub_rol);
    return subRol === "inventario" || role === "admin" || role === "gerente";
  }, [user?.role, user?.sub_rol]);
  // Solo admin (no gerente/inventario) puede mover Inactivo -> Mantenimiento/Baja
  // sin que la sucursal del equipo coincida con la sucursal del usuario/filtro.
  const isAdmin = useMemo(() => normalizeAccess(user?.role) === "admin", [user?.role]);
  const [tipo, setTipo] = useState(paramTipo || "equipo_modem");
  const [estado, setEstado] = useState(paramEstado || "Activos");
  const [sucursal, setSucursal] = useState(initialSucursal);
  const [buscar, setBuscar] = useState("");
  const [pageSize, setPageSize] = useState(30);
  const [appliedFilters, setAppliedFilters] = useState({
    tipo: paramTipo || "equipo_modem",
    estado: paramEstado || "Activos",
    sucursal: initialSucursal,
    buscar: "",
    pageSize: 30,
  });
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [changing, setChanging] = useState(false);
  const [error, setError] = useState("");
  const [dialogEquipo, setDialogEquipo] = useState(null);
  const [estadoDialog, setEstadoDialog] = useState(null);
  const [observacion, setObservacion] = useState("");
  const [selectedEquipos, setSelectedEquipos] = useState([]);
  const [exportando, setExportando] = useState(false);

  const handleExportar = async () => {
    setExportando(true);
    setError("");
    try {
      const { data: blob } = await exportarListadoCentroEquiposExcel({
        tipo: appliedFilters.tipo,
        estado: appliedFilters.estado,
        sucursal: appliedFilters.sucursal,
        buscar: appliedFilters.buscar,
      });
      descargarBlob(blob, "listado_equipos.xlsx");
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "No se pudo exportar a Excel");
    } finally {
      setExportando(false);
    }
  };

  useEffect(() => {
    if (data || !initialSucursal || initialSucursal === "Todos") return;
    setSucursal((current) => (current === "Todos" ? initialSucursal : current));
    setAppliedFilters((current) => (
      current.sucursal === "Todos" ? { ...current, sucursal: initialSucursal } : current
    ));
  }, [data, initialSucursal]);

  const rows = data?.items || [];
  const total = Number(data?.total || 0);
  const totalPages = Math.max(1, Math.ceil(total / appliedFilters.pageSize));

  const columns = useMemo(() => {
    if (appliedFilters.tipo !== "todos") return columnsByType[appliedFilters.tipo] || defaultColumns;
    return defaultColumns;
  }, [appliedFilters.tipo]);

  const fetchListado = useCallback(async (nextPage = page, filters = appliedFilters) => {
    try {
      setLoading(true);
      setError("");
      const params = {
        tipo: filters.tipo,
        estado: filters.estado,
        sucursal: filters.sucursal,
        buscar: filters.buscar,
        page: nextPage,
        pageSize: filters.pageSize,
      };
      const { data: payload } = await listarCentroEquipos(params);
      setAppliedFilters(filters);
      setData(payload || null);
      setPage(nextPage);
    } catch (e) {
      setData(null);
      setError(e?.response?.data?.message || e?.message || "No se pudo listar equipos");
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, page]);

  const handleBuscar = () => {
    setSelectedEquipos([]);
    fetchListado(1, { tipo, estado, sucursal, buscar: buscar.trim(), pageSize });
  };

  // Si se llegó con filtros por URL (deep link), dispara la búsqueda sola — sin esto,
  // el usuario tendría que hacer click en "Buscar" igual pese a venir con los filtros
  // ya elegidos.
  useEffect(() => {
    if (!tieneDeepLinkRef.current) return;
    tieneDeepLinkRef.current = false;
    fetchListado(1, {
      tipo: paramTipo || "equipo_modem",
      estado: paramEstado || "Activos",
      sucursal: initialSucursal,
      buscar: "",
      pageSize: 30,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // "Todos" normalmente no habilita selección/acciones (mezcla tipos con reglas
  // de estado distintas) — se habilita como excepción puntual cuando además el
  // filtro de Estado es Transicion, para poder recibir equipos de cualquier tipo
  // desde una sola pantalla (cada fila igual guarda su propio tipo_equipo real).
  const showActions = isActionType(appliedFilters.tipo)
    || (appliedFilters.tipo === "todos" && esEstadoTransicion(appliedFilters.estado));
  const selectedEstados = useMemo(
    () => Array.from(new Set(selectedEquipos.map((item) => normalizeAccess(item.estado)))),
    [selectedEquipos]
  );
  const onlySelectedInactivo = selectedEquipos.length > 0 && selectedEstados.length === 1 && selectedEstados[0] === "inactivo";
  const onlySelectedAlmacen = selectedEquipos.length > 0 && selectedEstados.length === 1 && selectedEstados[0] === "almacen";
  const onlySelectedMantenimiento = selectedEquipos.length > 0 && selectedEstados.length === 1 && selectedEstados[0] === "mantenimiento";
  const onlySelectedTransicion = selectedEquipos.length > 0 && selectedEstados.length === 1 && ["transicion", "transsicion", "trancicion"].includes(selectedEstados[0]);
  const onlySelectedBaja = selectedEquipos.length > 0 && selectedEstados.length === 1 && selectedEstados[0] === "baja";
  const onlySelectedRobado = selectedEquipos.length > 0 && selectedEstados.length === 1 && selectedEstados[0] === "robado";
  const onlySelectedInventarioEditable = onlySelectedInactivo || onlySelectedAlmacen;
  const onlySelectedCanGoInactivo =
    onlySelectedMantenimiento ||
    onlySelectedBaja ||
    onlySelectedRobado ||
    (canManageInventory && onlySelectedAlmacen);
  const onlySelectedCanGoBaja = canManageInventory
    ? (onlySelectedInactivo || onlySelectedAlmacen || onlySelectedMantenimiento)
    : (onlySelectedInactivo || onlySelectedMantenimiento);
  const onlySelectedCanGoMantenimiento = canManageInventory ? onlySelectedInventarioEditable : onlySelectedInactivo;
  const onlySelectedCanGoAlmacen = canManageInventory && onlySelectedInactivo;

  const isEligible = (row) => {
    const estadoRow = normalizeAccess(row?.estado);
    const esTransicion = ["transicion", "transsicion", "trancicion"].includes(estadoRow);
    const equipoMantenimientoConCliente =
      estadoRow === "mantenimiento" &&
      hasClienteRelacionado(row);

    // "baja" y "robado" son seleccionables para cualquier usuario (pueden pasar a Inactivo,
    // y "baja" también a Robado); "almacen" sigue exclusivo de quienes gestionan inventario.
    // El chequeo de sucursal más abajo (misma lógica para todos los estados no-transición)
    // ya se encarga de que solo se pueda operar sobre equipos de la propia sucursal.
    const estadoPermitido = canManageInventory
      ? ["inactivo", "almacen", "mantenimiento", "transicion", "transsicion", "trancicion", "baja", "robado"].includes(estadoRow)
      : ["inactivo", "mantenimiento", "transicion", "transsicion", "trancicion", "baja", "robado"].includes(estadoRow);

    // Con Tipo=Todos no hay un tipo unico contra el cual comparar — alcanza con
    // que la fila sea de alguno de los tipos que sí soportan cambio de estado.
    const tipoCoincide = appliedFilters.tipo === "todos"
      ? isActionType(row?.tipo_equipo)
      : row?.tipo_equipo === appliedFilters.tipo;

    // Excepcion puntual: admin puede operar un equipo Inactivo de cualquier sucursal
    // (para poder pasarlo a Mantenimiento o Baja sin importar donde esta registrado).
    const sucursalLibrePorAdmin = isAdmin && estadoRow === "inactivo";

    return showActions &&
      tipoCoincide &&
      estadoPermitido &&
      !equipoMantenimientoConCliente &&
      (esTransicion
        ? sameText(row?.sucursal, initialSucursal)
        : (sucursalLibrePorAdmin || !row?.sucursal || sameText(row?.sucursal, initialSucursal)));
  };

  const toggleEquipo = (row) => {
    const id = getDetalleId(row);
    const tipoEquipo = row?.tipo_equipo || appliedFilters.tipo;
    if (!id || !isEligible(row)) return;
    setSelectedEquipos((current) => (
      current.some((item) => item.id === id && item.tipo === tipoEquipo)
        ? current.filter((item) => !(item.id === id && item.tipo === tipoEquipo))
        : [...current, { tipo: tipoEquipo, id, estado: row?.estado }]
    ));
  };

  const openEstadoDialog = (estadoDestino) => {
    if (!selectedEquipos.length) return;
    setEstadoDialog(estadoDestino);
    setObservacion("");
  };

  const closeEstadoDialog = () => {
    if (changing) return;
    setEstadoDialog(null);
    setObservacion("");
  };

  const cambiarSeleccionados = async () => {
    if (!selectedEquipos.length) return;
    try {
      setChanging(true);
      setError("");
      await Promise.all(selectedEquipos.map((item) => cambiarEstadoEquipoCentro(item.tipo, item.id, estadoDialog, observacion)));
      setEstadoDialog(null);
      setObservacion("");
      setSelectedEquipos([]);
      await fetchListado(page, appliedFilters);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "No se pudo cambiar el estado");
    } finally {
      setChanging(false);
    }
  };

  const openDetalle = (row) => {
    const detalleTipo = toDetalleTipo(row?.tipo_equipo || appliedFilters.tipo);
    const detalleId = getDetalleId(row);

    if (!detalleTipo || !detalleId) {
      setError("Este tipo de equipo todavia no tiene detalle disponible.");
      return;
    }

    setDialogEquipo({
      tipo: detalleTipo,
      id: detalleId,
      titulo: `${row?.tipo_equipo || "Equipo"} ${detalleId}`,
    });
  };

  return (
    <Paper variant="outlined" sx={{ p: 0.75, borderRadius: 0.75 }}>
      <Grid container spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <FormControl size="small" fullWidth sx={controlSx}>
            <InputLabel>Tipo de equipo</InputLabel>
            <Select label="Tipo de equipo" value={tipo} onChange={(e) => setTipo(e.target.value)} sx={{ fontSize: 12 }}>
              {TIPOS.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <FormControl size="small" fullWidth sx={controlSx}>
            <InputLabel>Estado</InputLabel>
            <Select label="Estado" value={estado} onChange={(e) => setEstado(e.target.value)} sx={{ fontSize: 12 }}>
              {ESTADOS.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 1.8 }}>
          <FormControl size="small" fullWidth sx={controlSx}>
            <InputLabel>Sucursal</InputLabel>
            <Select label="Sucursal" value={sucursal} onChange={(e) => setSucursal(e.target.value)} sx={{ fontSize: 12 }}>
              {SUCURSALES.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.2 }}>
          <TextField
            size="small"
            label="Buscar equipo"
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
            onKeyDown={(e) => {
              // Enter simple inserta salto de línea (para poder pegar/escribir
              // varios identificadores); Ctrl/Cmd+Enter dispara la búsqueda —
              // mismo criterio que TraspasoEquipos.jsx.
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) handleBuscar();
            }}
            placeholder="SIM, chip, serie, alias... (pega una columna de Excel)"
            multiline
            minRows={1}
            maxRows={4}
            fullWidth
            sx={{
              ...controlSx,
              "& .MuiOutlinedInput-root": { minHeight: 30, fontSize: 11.5, alignItems: "flex-start" },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 1 }}>
          <FormControl size="small" fullWidth sx={controlSx}>
            <InputLabel>Limite</InputLabel>
            <Select label="Limite" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} sx={{ fontSize: 12 }}>
              {[20, 30, 50, 100].map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, md: 1 }}>
          <Button
            fullWidth
            variant="contained"
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <SearchIcon fontSize="small" />}
            onClick={handleBuscar}
            sx={{ minHeight: 30, height: 30, fontSize: 11.5, px: 1.25 }}
          >
            Buscar
          </Button>
        </Grid>
      </Grid>

      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      {loading && <LinearProgress sx={{ mb: 0.75 }} />}

      <Stack direction={{ xs: "column", md: "row" }} spacing={0.75} alignItems={{ xs: "stretch", md: "center" }} sx={{ mb: 0.75 }}>
        <Chip size="small" label={`Total: ${total}`} sx={{ height: 20, fontSize: 11 }} />
        <Chip size="small" label={`Pagina: ${data ? page : 0} / ${data ? totalPages : 0}`} sx={{ height: 20, fontSize: 11 }} />
        <Button
          size="small"
          variant="outlined"
          startIcon={exportando ? <CircularProgress size={12} color="inherit" /> : <DownloadIcon fontSize="small" />}
          disabled={!data || loading || exportando}
          onClick={handleExportar}
          sx={{ minHeight: 24, height: 24, fontSize: 10.5 }}
        >
          {exportando ? "Exportando..." : "Exportar Excel"}
        </Button>
        {showActions && (
          <>
            <Chip size="small" color="warning" variant="outlined" label={`Seleccionados: ${selectedEquipos.length}`} sx={{ height: 20, fontSize: 11 }} />
            <Button
              size="small"
              variant="contained"
              color="success"
              disabled={!onlySelectedCanGoAlmacen || changing}
              onClick={() => openEstadoDialog("Almacen")}
              sx={{ minHeight: 24, height: 24, fontSize: 10.5 }}
            >
              Almacen
            </Button>
            <Button
              size="small"
              variant="contained"
              color="success"
              disabled={!onlySelectedTransicion || changing}
              onClick={() => openEstadoDialog(canManageInventory ? "Almacen" : "Inactivo")}
              sx={{ minHeight: 24, height: 24, fontSize: 10.5 }}
            >
              Recibido
            </Button>
            <Button
              size="small"
              variant="contained"
              color="info"
              disabled={!onlySelectedCanGoInactivo || changing}
              onClick={() => openEstadoDialog("Inactivo")}
              sx={{ minHeight: 24, height: 24, fontSize: 10.5 }}
            >
              Inactivo
            </Button>
            <Button
              size="small"
              variant="contained"
              color="warning"
              disabled={!onlySelectedCanGoMantenimiento || changing}
              onClick={() => openEstadoDialog("Mantenimiento")}
              sx={{ minHeight: 24, height: 24, fontSize: 10.5 }}
            >
              Mantenimiento
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              disabled={!onlySelectedCanGoBaja || changing}
              onClick={() => openEstadoDialog("Baja")}
              sx={{ minHeight: 24, height: 24, fontSize: 10.5 }}
            >
              Baja
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              disabled={!onlySelectedBaja || changing}
              onClick={() => openEstadoDialog("Robado")}
              sx={{ minHeight: 24, height: 24, fontSize: 10.5 }}
            >
              Robado
            </Button>
          </>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Stack direction="row" spacing={0.5}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<NavigateBeforeIcon />}
            disabled={!data || loading || page <= 1}
            onClick={() => fetchListado(page - 1)}
            sx={{ minHeight: 26, height: 26, fontSize: 11 }}
          >
            Anterior
          </Button>
          <TextField
            size="small"
            type="number"
            value={data ? page : ""}
            disabled={!data || loading}
            onChange={(e) => {
              const next = Number(e.target.value);
              if (next >= 1 && next <= totalPages) fetchListado(next);
            }}
            inputProps={{ min: 1, max: totalPages, style: { textAlign: "center", padding: "2px 4px", fontSize: 11 } }}
            sx={{ width: 58, "& .MuiOutlinedInput-root": { height: 26 } }}
          />
          <Button
            size="small"
            variant="outlined"
            endIcon={<NavigateNextIcon />}
            disabled={!data || loading || page >= totalPages}
            onClick={() => fetchListado(page + 1)}
            sx={{ minHeight: 26, height: 26, fontSize: 11 }}
          >
            Siguiente
          </Button>
        </Stack>
      </Stack>

      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: "calc(100vh - 220px)", borderRadius: 0.5 }}>
        <Table size="small" stickyHeader sx={{ "& .MuiTableCell-root": { py: 0.22, px: 0.55, fontSize: 11 } }}>
          <TableHead>
            <TableRow>
              {showActions && (
                <TableCell
                  sx={{
                    fontWeight: 800,
                    bgcolor: (theme) =>
                      theme.palette.mode === "dark"
                        ? theme.palette.background.paper
                        : theme.palette.grey[100],
                    width: 34,
                  }}
                >
                  Sel.
                </TableCell>
              )}
              {columns.map(([, label]) => (
                <TableCell
                  key={label}
                  sx={{
                    fontWeight: 800,
                    bgcolor: (theme) =>
                      theme.palette.mode === "dark"
                        ? theme.palette.background.paper
                        : theme.palette.grey[100],
                  }}
                >
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {!rows.length && !loading ? (
              <TableRow>
                <TableCell colSpan={columns.length + (showActions ? 1 : 0)}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                    Selecciona filtros y presiona Buscar.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : rows.map((row, index) => (
              <TableRow key={`${row.tipo_equipo || tipo}-${row.id || row.identificador}-${index}`} hover>
                {showActions && (
                  <TableCell>
                    <Checkbox
                      size="small"
                      checked={selectedEquipos.some((item) => item.id === getDetalleId(row) && item.tipo === (row?.tipo_equipo || appliedFilters.tipo))}
                      disabled={!isEligible(row) || changing}
                      onChange={() => toggleEquipo(row)}
                      sx={{ p: 0 }}
                    />
                  </TableCell>
                )}
                {columns.map(([key]) =>
                  key === "identificador" ? (
                    <TableCell key={key} title={fmt(row[key])}>
                      <Typography
                        component="span"
                        role="button"
                        tabIndex={0}
                        variant="caption"
                        noWrap
                        onClick={() => openDetalle(row)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") openDetalle(row);
                        }}
                        sx={{
                          display: "block",
                          maxWidth: cellMaxWidth(key),
                          lineHeight: 1.25,
                          fontWeight: 700,
                          color: "#006d77",
                          textDecoration: "underline",
                          cursor: "pointer",
                        }}
                      >
                        {fmt(row[key])}
                      </Typography>
                    </TableCell>
                  ) : (
                    <TableCell key={key} title={fmt(row[key])}>
                      <Typography
                        variant="caption"
                        noWrap={!NO_TRUNCATE_COLUMNS.has(key)}
                        sx={{ display: "block", maxWidth: cellMaxWidth(key), lineHeight: 1.25 }}
                      >
                        {fmt(row[key])}
                      </Typography>
                    </TableCell>
                  )
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={Boolean(dialogEquipo)}
        onClose={() => setDialogEquipo(null)}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { borderRadius: 1 } }}
      >
        <DialogTitle sx={{ py: 0.75, px: 1.25, fontSize: 14, fontWeight: 800 }}>
          {dialogEquipo?.titulo || "Detalle de equipo"}
          <IconButton
            size="small"
            onClick={() => setDialogEquipo(null)}
            sx={{ position: "absolute", right: 8, top: 5 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0.75 }}>
          {dialogEquipo && (
            <BusquedaPorIdentificador
              initialTipo={dialogEquipo.tipo}
              initialBuscar={dialogEquipo.id}
              autoLoadKey={`${dialogEquipo.tipo}-${dialogEquipo.id}`}
              hideSearch
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(estadoDialog)}
        onClose={closeEstadoDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 1 } }}
      >
        <DialogTitle sx={{ py: 0.9, px: 1.25, fontSize: 14, fontWeight: 800 }}>
          Cambiar estado a {estadoDialog}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 1 }}>
          <Stack spacing={1}>
            <Alert severity="info" sx={{ py: 0, fontSize: 12 }}>
              Se actualizaran {selectedEquipos.length} equipo(s) seleccionados.
            </Alert>
            <TextField
              label="Observacion"
              value={observacion}
              onChange={(e) => setObservacion(e.target.value)}
              multiline
              minRows={3}
              maxRows={6}
              fullWidth
              size="small"
              placeholder="Detalle del cambio de estado..."
              inputProps={{ maxLength: 500 }}
            />
            <Typography variant="caption" color="text.secondary">
              {observacion.length}/500
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 1, py: 0.75 }}>
          <Button size="small" onClick={closeEstadoDialog} disabled={changing}>
            Cancelar
          </Button>
          <Button
            size="small"
            variant="contained"
            color={estadoDialog === "Baja" ? "error" : "warning"}
            onClick={cambiarSeleccionados}
            disabled={changing}
          >
            {changing ? "Guardando..." : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
